import { useEffect, useRef, useState } from "react";
import { ChevronsUpDown, Check } from "lucide-react";

export const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda","Argentina","Armenia","Australia","Austria",
  "Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan",
  "Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cabo Verde","Cambodia",
  "Cameroon","Canada","Central African Republic","Chad","Chile","China","Colombia","Comoros","Congo","Costa Rica",
  "Croatia","Cuba","Cyprus","Czech Republic","Denmark","Djibouti","Dominica","Dominican Republic","Ecuador","Egypt",
  "El Salvador","Equatorial Guinea","Eritrea","Estonia","Eswatini","Ethiopia","Fiji","Finland","France","Gabon",
  "Gambia","Georgia","Germany","Ghana","Greece","Grenada","Guatemala","Guinea","Guinea-Bissau","Guyana",
  "Haiti","Honduras","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland","Israel",
  "Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kiribati","Kuwait","Kyrgyzstan","Laos",
  "Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein","Lithuania","Luxembourg","Madagascar","Malawi",
  "Malaysia","Maldives","Mali","Malta","Marshall Islands","Mauritania","Mauritius","Mexico","Micronesia","Moldova",
  "Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia","Nauru","Nepal","Netherlands",
  "New Zealand","Nicaragua","Niger","Nigeria","North Korea","North Macedonia","Norway","Oman","Pakistan","Palau",
  "Palestine","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland","Portugal","Qatar","Romania",
  "Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines","Samoa","San Marino","Sao Tome and Principe","Saudi Arabia","Senegal",
  "Serbia","Seychelles","Sierra Leone","Singapore","Slovakia","Slovenia","Solomon Islands","Somalia","South Africa","South Korea",
  "South Sudan","Spain","Sri Lanka","Sudan","Suriname","Sweden","Switzerland","Syria","Taiwan","Tajikistan",
  "Tanzania","Thailand","Timor-Leste","Togo","Tonga","Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Tuvalu",
  "Uganda","Ukraine","United Arab Emirates","United Kingdom","United States","Uruguay","Uzbekistan","Vanuatu","Vatican City","Venezuela",
  "Vietnam","Yemen","Zambia","Zimbabwe",
];

export function CountrySelect({ value, onChange, testId = "customer-input-country" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const filtered = search
    ? COUNTRIES.filter((c) => c.toLowerCase().includes(search.toLowerCase()))
    : COUNTRIES;

  useEffect(() => {
    if (!open) { setSearch(""); return; }
    setTimeout(() => searchRef.current?.focus(), 0);
    const onDown = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onDown);
    return () => document.removeEventListener("keydown", onDown);
  }, [open]);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  return (
    <div ref={containerRef} style={{ position: "relative" }} data-testid={`${testId}-wrapper`}>
      <button
        type="button"
        className="input-base flex items-center justify-between"
        style={{ textAlign: "left", width: "100%" }}
        data-testid={testId}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
      >
        <span style={{ color: value ? "inherit" : "var(--text-2)" }}>{value || "Select country…"}</span>
        <ChevronsUpDown size={14} style={{ opacity: 0.5, flexShrink: 0 }} />
      </button>

      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
            background: "var(--surface-1, #fff)", border: "1px solid var(--border, #e2e2e2)",
            borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            zIndex: 9999, overflow: "hidden",
          }}
        >
          <div style={{ padding: "8px 10px", borderBottom: "1px solid var(--border, #e2e2e2)" }}>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search country…"
              className="input-base"
              style={{ padding: "6px 10px", fontSize: 13 }}
            />
          </div>
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {value && (
              <button
                type="button"
                className="btn-ghost"
                style={{ width: "100%", textAlign: "left", padding: "8px 12px", fontSize: 13, color: "var(--text-2)", display: "block" }}
                onClick={() => { onChange(""); setOpen(false); }}
              >
                — Clear —
              </button>
            )}
            {filtered.length === 0 && (
              <div style={{ padding: "12px", fontSize: 13, color: "var(--text-2)", textAlign: "center" }}>No country found.</div>
            )}
            {filtered.map((c) => (
              <button
                key={c}
                type="button"
                className="btn-ghost"
                style={{
                  width: "100%", textAlign: "left", padding: "8px 12px", fontSize: 13,
                  display: "flex", alignItems: "center", gap: 8,
                  background: c === value ? "var(--surface-2, #f5f5f5)" : undefined,
                }}
                onClick={() => { onChange(c); setOpen(false); }}
              >
                <Check size={13} style={{ opacity: c === value ? 1 : 0, flexShrink: 0 }} />
                {c}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CountrySelect;
